import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export function FollowUpDialog({ open, onOpenChange, lead, followup, onSaved }) {
  const isReschedule = !!followup;
  const [date, setDate] = useState(followup?.due_date || "");
  const [time, setTime] = useState(followup?.due_time || "");
  const [reason, setReason] = useState(followup?.reason || "");
  const [note, setNote] = useState(followup?.note || "");
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Bitte ein Datum wählen"); return; }
    setSaving(true);
    try {
      if (isReschedule) {
        await api.patch(`/followups/${followup.id}`, {
          action: "reschedule", due_date: date, due_time: time || null, reason, note,
        });
        toast.success("Follow-up verschoben");
      } else {
        await api.post(`/leads/${lead.id}/followups`, {
          due_date: date, due_time: time || null, reason, note,
        });
        toast.success("Follow-up geplant");
      }
      qc.invalidateQueries();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="followup-dialog">
        <DialogHeader>
          <DialogTitle>{isReschedule ? "Follow-up verschieben" : "Follow-up planen"}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{lead?.company || "Termin festlegen"}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Datum</Label>
              <Input data-testid="followup-date-input" type="date" value={date}
                onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <Label>Uhrzeit (optional)</Label>
              <Input data-testid="followup-time-input" type="time" value={time}
                onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Grund</Label>
            <Input data-testid="followup-reason-input" value={reason}
              onChange={(e) => setReason(e.target.value)} placeholder="z.B. Angebot nachfassen" />
          </div>
          <div>
            <Label>Notiz</Label>
            <Textarea data-testid="followup-note-input" value={note} rows={2}
              onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" data-testid="followup-submit-btn" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
